import { z } from 'zod';

/** The optional "What would you like help with?" note on the Talk to Nathan form. */
const NoteSchema = z.string().trim().min(1).max(1000);

const ConsultationRequestSchema = z.object({
  kind: z.literal('consultation'),
  offer: z.enum(['service-hub', 'structured-data', 'aeo-healthcare']),
  message: NoteSchema.optional(),
});

/**
 * A plain "Talk to us" request that carries a note. A request with no note
 * stores no context at all, so this kind always has a message.
 */
const NoteRequestSchema = z.object({
  kind: z.literal('note'),
  message: NoteSchema,
});

const ManualReviewRequestSchema = z.object({
  kind: z.literal('manual-review'),
  checkId: z.string().min(1).max(100),
  website: z
    .url()
    .max(2048)
    .refine((value) => /^https?:\/\//i.test(value), 'Use an http or https website'),
  location: z.string().trim().min(2, 'Enter your city and state').max(150),
  failureCode: z.enum(['LOCATION_NOT_FOUND', 'INSUFFICIENT_DATA', 'RESEARCH_FAILED']),
});

/**
 * A message sent from a completed discovery report (HAL-2081). The kind keeps
 * its original name so stored rows and the ads readout's `report_question`
 * stage stay continuous, but the note is optional: an empty message is a
 * reader who wants to talk without a specific question, and the report itself
 * tells Nathan the practice and its findings. The report is joined on
 * `request_context->>'checkId'`, never on the lead row's `check_id`: a lead
 * with `check_id` set reads as a report unlock.
 */
const ReportQuestionRequestSchema = z.object({
  kind: z.literal('report-question'),
  checkId: z.string().min(1).max(100),
  practiceName: z.string().trim().min(1).max(200),
  message: z.string().trim().max(1000).default(''),
});

/** Context retained with a lead and included in operator alerts, never analytics PII. */
export const BookingRequestContextSchema = z.discriminatedUnion('kind', [
  ConsultationRequestSchema,
  NoteRequestSchema,
  ManualReviewRequestSchema,
  ReportQuestionRequestSchema,
]);
export type BookingRequestContext = z.infer<typeof BookingRequestContextSchema>;

/** Shared operator-facing description for inline and backstop notifications. */
export function describeBookingRequest(context?: BookingRequestContext | null): string | null {
  if (!context) return null;
  if (context.kind === 'consultation') {
    return `Consultation offer: ${context.offer}${context.message ? `. Note: ${context.message}` : ''}`;
  }
  if (context.kind === 'note') return `Note: ${context.message}`;
  if (context.kind === 'report-question') {
    return context.message
      ? `Message about the ${context.practiceName} report (${context.checkId}): ${context.message}`
      : `Message from the ${context.practiceName} report (${context.checkId}), with no note`;
  }
  return `Manual review requested. Website: ${context.website}. Location: ${context.location}. Failed check: ${context.checkId} (${context.failureCode}). No completed report is available.`;
}

/**
 * What the browser submits. The same as the stored context except that a
 * report question carries no practice name: the server reads it from the
 * saved check rather than trusting the page.
 */
export const BookingRequestSubmissionSchema = z.discriminatedUnion('kind', [
  ConsultationRequestSchema,
  NoteRequestSchema,
  ManualReviewRequestSchema,
  ReportQuestionRequestSchema.omit({ practiceName: true }),
]);
export type BookingRequestSubmission = z.infer<typeof BookingRequestSubmissionSchema>;

/**
 * What opens the modal on a request. The modal collects the note, the
 * manual-review location and the report message after opening; the server
 * fills the practice name. A plain note is built on submit, never an opener.
 */
export type BookingModalRequest =
  | Omit<Extract<BookingRequestContext, { kind: 'consultation' }>, 'message'>
  | Omit<Extract<BookingRequestContext, { kind: 'manual-review' }>, 'location'>
  | Omit<Extract<BookingRequestContext, { kind: 'report-question' }>, 'message' | 'practiceName'>;
